'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTABanner from '@/components/CTABanner';
import FAQAccordion from '@/components/FAQAccordion';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const faqCategories = [
  {
    category: 'Featured — Loan Process',
    items: [
      {
        question: 'How long will it take to receive my funds?',
        answer:
          'Timing for receiving approved funding depends on many factors. Each lending partner has its own approval process and can result in various funding timelines. The typical time to fund ranges from same-day to several business days after approval.',
      },
    ],
  },
  {
    category: 'Loan Types',
    items: [
      {
        question: 'What is a line of credit?',
        answer:
          'A line of credit is a revolving form of credit that provides a predetermined capital limit and can be accessed as needed. Unlike a traditional term loan, all or part of the line can be accessed at any time up to the predetermined limit. Interest is only paid upon the amount actually used.',
      },
      {
        question: 'Is factoring a loan?',
        answer:
          "Technically factoring is not a loan; it is the purchase of future receivables. A third party, known as a factor, purchases a company's invoice(s) at a discount giving a business owner access to a percentage of that invoice upfront, instead of when the invoice is paid.",
      },
    ],
  },
  {
    category: 'Applying',
    items: [
      {
        question: 'Can I get a business loan after bankruptcy?',
        answer:
          "A bankruptcy in your past doesn't necessarily preclude you from getting a small business loan, but it might make it more challenging. While not all lenders have the same requirements after bankruptcy, it's unlikely a borrower would qualify within the first year. Many lenders will require at least one year of improving credit history following the disposition of a bankruptcy.",
      },
      {
        question: 'What is collateral?',
        answer:
          'Collateral is any asset or assets, which can be offered by a borrower to secure a loan. Should a borrower default, the lender can take possession of the asset, or assets, to satisfy the loan.',
      },
      {
        question: 'What is a personal guarantee on a small business loan?',
        answer:
          "It's common practice for lenders to require a personal guarantee from the business owner(s) to protect the lender should the business default on the loan. Lenders do this to mitigate the risk of lending to small businesses, and the guarantee is often a requirement by the lender before offering a loan.",
      },
      {
        question: 'Do I need a business plan to get a loan?',
        answer:
          "That all depends upon the type of loan you're looking for. To qualify for an SBA loan you'll need a business plan. While other lenders might not require a formal business plan, they will ask questions about loan purpose, how this loan might positively impact profitability, etc.",
      },
      {
        question: 'What is a business loan?',
        answer:
          'A business loan is a type of loan designed for businesses to help them finance their operations, payroll, equipment and expansion opportunities.',
      },
      {
        question: 'How do I qualify for a business loan?',
        answer:
          'To qualify for a business loan, lenders typically look at your credit score, time in business, and your business bank statements to evaluate cash flow. Depending on the lender, they may ask for additional information during the underwriting process.',
      },
    ],
  },
  {
    category: 'Loan Process',
    items: [
      {
        question: 'What is the difference between personal credit score and business credit profile?',
        answer:
          'A personal credit score is a reflection of how someone repays their mortgage, auto loans, or other personal obligations and is typically demonstrated in a score from 300 to 850. The higher the score the better. A business credit profile reflects how a business owner meets their business financial obligations. The three primary personal credit bureaus are: Experian, Equifax, and Transunion. The three primary business credit bureaus are: Dunn & Bradstreet, Experian, and Equifax.',
      },
    ],
  },
];

export default function FAQPage() {
  useScrollReveal();

  return (
    <>
      <Navbar />
      <main>
        {/* HERO */}
        <section
          style={{
            background: '#f7f9fc',
            padding: '140px 60px 60px',
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h1 className="reveal" style={{ marginBottom: '16px' }}>Frequently Asked Questions</h1>
            <p
              className="reveal"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1.05rem',
                color: '#4a5568',
                lineHeight: 1.75,
              }}
            >
              Everything you need to know about business loans and the Lendo process.
            </p>
          </div>
        </section>

        {/* FAQ BODY */}
        <section
          style={{
            background: '#ffffff',
            padding: '80px 60px',
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {faqCategories?.map((cat) => (
              <div key={cat?.category} className="reveal">
                <h3
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#1a2b4a',
                    marginTop: '48px',
                    marginBottom: '16px',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #e2e8f0',
                  }}
                >
                  {cat?.category}
                </h3>
                <FAQAccordion items={cat?.items} />
              </div>
            ))}
          </div>
        </section>

        {/* CTA BANNER */}
        <CTABanner />
      </main>
      <Footer />
      <style>{`
        @media (max-width: 768px) {
          section {
            padding: 60px 24px !important;
          }
        }
      `}</style>
    </>
  );
}
